import { useEffect, useState } from 'react'

import { useDeviceDrawerData } from '../../hooks/useDeviceDrawerData'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useTranslation } from 'react-i18next'

import { Box, Flex, Skeleton } from '@hivewatch/dark'

import { EditDeviceFieldsI } from '@/features/device-crud'
import {
  DRAWER_VIDEO_PLAYER_HEIGHT,
  DRAWER_VIDEO_PLAYER_WIDTH,
  LazyLiveKitVideoPlayer,
  MSEVideoPlayer,
  VideoFeedEvent,
  VideoPlayer,
  useCameraFeed,
} from '@/features/video-player'
import { DeviceType } from '@/graphql/generated/schemas'
import {
  AnalyticsScreen,
  BUTTON_PRESS,
  trackEvent,
  useMixpanel,
} from '@/utils/analytics'

import { DarkDeviceDrawer } from './DarkDeviceDrawer'

interface IProps {
  defaultIndex: number
  deviceId: string
  isOpen: boolean
  onClose: () => void
  onEditClick: (deviceData: EditDeviceFieldsI) => void
}

/**
 * Rebrand (ENG-32123) camera device drawer. Wraps the shared `DarkDeviceDrawer`
 * chrome and supplies the camera video region. The video feed logic is
 * unchanged from the legacy `CameraDeviceDrawer` — the dark video-player
 * component is a later phase.
 */
export const DarkCameraDeviceDrawer = ({
  defaultIndex,
  deviceId,
  isOpen,
  onClose,
  onEditClick,
}: IProps) => {
  const { t } = useTranslation()
  const { device, incidents, isIncidentsLoading, isLoading } =
    useDeviceDrawerData(deviceId)
  const [isPipActive, setPIPActive] = useState(false)

  // Feature flag for MSE-based authenticated proxy streaming
  const isMSEStreamingEnabled = useFeatureFlag(
    FeatureFlag.authenticatedProxyStreaming
  )
  // Feature flag for Lumana cloud WebRTC live view (ENG-31640)
  const isLumanaStreamEnabled = useFeatureFlag(FeatureFlag.lumanaCameraStream)

  const {
    createCameraEventHandler,
    createPlayerActionHandler,
    markStreamRequested,
  } = useMixpanel(AnalyticsScreen.DeviceDrawer)

  useEffect(() => {
    if (isOpen) {
      markStreamRequested()
    }
  }, [isOpen])

  const mixpanelData = () => {
    return {
      device_id: deviceId,
      facility_id: device?.facility?.id,
      floor_id: device?.floor?.id,
      floor_name: device?.floor?.name,
      streaming_type: device?.camera?.streamingType,
    }
  }
  const handleCameraEvent = createCameraEventHandler(mixpanelData())
  const handlePlayerAction = createPlayerActionHandler(mixpanelData())

  const {
    url: cameraFeedUrl,
    isLoading: isFeedDataLoading,
    errorMessage,
    stream,
  } = useCameraFeed(deviceId, handleCameraEvent)

  const disablePIP = () => setPIPActive(false)

  const handleEditClick = () => {
    if (device) {
      trackEvent(`${BUTTON_PRESS} CameraDrawer Edit Camera`, mixpanelData())
      onEditClick({
        id: deviceId,
        floorId: device.floor?.id,
        name: device.name,
        type: DeviceType.Camera,
      })
    }
  }

  const videoSlot = isFeedDataLoading ? (
    <Skeleton
      borderRadius='0.375rem'
      display='block'
      h={DRAWER_VIDEO_PLAYER_HEIGHT}
      maxW='100%'
      w={DRAWER_VIDEO_PLAYER_WIDTH}
    />
  ) : isLumanaStreamEnabled && stream?.kind === 'livekit' ? (
    <LazyLiveKitVideoPlayer
      apiUrl={stream.apiUrl}
      cameraId={deviceId}
      errorLabel={errorMessage}
      onEvent={handleCameraEvent}
      onPlayerControlAction={handlePlayerAction}
      onStartPlaying={() => handleCameraEvent(VideoFeedEvent.Playing)}
      placeholderHeight={DRAWER_VIDEO_PLAYER_HEIGHT}
      placeholderWidth={DRAWER_VIDEO_PLAYER_WIDTH}
      token={stream.token}
    />
  ) : cameraFeedUrl ? (
    isMSEStreamingEnabled ? (
      <MSEVideoPlayer
        autoplay={true}
        cameraId={deviceId}
        errorLabel={errorMessage}
        onPlayerControlAction={handlePlayerAction}
        onStartPlaying={() => handleCameraEvent(VideoFeedEvent.Playing)}
        placeholderHeight={DRAWER_VIDEO_PLAYER_HEIGHT}
        placeholderWidth={DRAWER_VIDEO_PLAYER_WIDTH}
      />
    ) : (
      <VideoPlayer
        disablePIP={disablePIP}
        errorLabel={errorMessage}
        liveEnabled
        liveOnly
        onPlayerControlAction={handlePlayerAction}
        onStartPlaying={() => handleCameraEvent(VideoFeedEvent.Playing)}
        pipActive={isPipActive}
        placeholderHeight={DRAWER_VIDEO_PLAYER_HEIGHT}
        placeholderWidth={DRAWER_VIDEO_PLAYER_WIDTH}
        url={cameraFeedUrl}
      />
    )
  ) : (
    <Flex
      alignItems='center'
      bg='black'
      borderRadius='0.375rem'
      h={DRAWER_VIDEO_PLAYER_HEIGHT}
      justifyContent='center'
      w={DRAWER_VIDEO_PLAYER_WIDTH}
    >
      <Box color='white'>{t('devices:drawer_video_unavailable')}</Box>
    </Flex>
  )

  return (
    <DarkDeviceDrawer
      defaultIndex={defaultIndex}
      device={device}
      deviceId={deviceId}
      deviceType={DeviceType.Camera}
      incidents={incidents}
      isIncidentsLoading={isIncidentsLoading}
      isLoading={isLoading}
      isOpen={isOpen}
      onClose={onClose}
      onEdit={handleEditClick}
      videoSlot={videoSlot}
    />
  )
}
